import React, { useState, useEffect } from 'react';
import { authStorage } from '../../utils/storage';
import { StickyNote, Star, Trash2, Pencil, X, Plus } from 'lucide-react';
import DOMPurify from 'dompurify';
import RichTextEditor from './RichTextEditor';

const NotesPanel = ({ bargainingUnitId }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newNoteImportant, setNewNoteImportant] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [isComposerOpen, setIsComposerOpen] = useState(false);

  const username = authStorage.getUsername();
  const draftKeyNew = `notesDraft:new:${bargainingUnitId}:${username}`;
  const draftKeyEdit = (id) => `notesDraft:edit:${id}:${username}`;

  const loadDraft = (key) => { try { return localStorage.getItem(key) || ''; } catch { return ''; } };
  const saveDraft = (key, html) => { try { localStorage.setItem(key, sanitize(html)); } catch {} };
  const clearDraft = (key) => { try { localStorage.removeItem(key); } catch {} };
  const sanitize = (html) => DOMPurify.sanitize(html || '', { USE_PROFILES: { html: true } });

  const hasHtmlTags = (str = '') => /<\/?[a-z][\s\S]*>/i.test(str);
  const decodeEntities = (str = '') => {
    const el = document.createElement('textarea'); el.innerHTML = str; return el.value;
  };
  const escapeHtml = (str = '') => str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const toHtml = (str = '') => {
    if (!str) return '';
    const decoded = decodeEntities(str);
    if (hasHtmlTags(decoded)) return sanitize(decoded);
    return `<p>${escapeHtml(decoded).replace(/\n/g, '<br>')}</p>`;
  };
  const toPlainText = (html = '') => {
    const el = document.createElement('div'); el.innerHTML = html;
    return (el.textContent || el.innerText || '').trim();
  };

  useEffect(() => { fetchNotes(); }, [bargainingUnitId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const draft = loadDraft(draftKeyNew);
    if (draft) { setNewNote(draft); setIsComposerOpen(true); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { saveDraft(draftKeyNew, newNote); }, [newNote]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (editingNoteId) saveDraft(draftKeyEdit(editingNoteId), editContent);
  }, [editContent, editingNoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const trackEvent = async (noteId, event) => {
    try {
      const token = authStorage.getToken();
      if (!token) return;
      await fetch(`/api/notes/${noteId}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ event }),
      });
    } catch (_) {}
  };

  const fetchNotes = async () => {
    const token = authStorage.getToken();
    if (!token) { setLoading(false); return; }
    try {
      const res = await fetch(`/api/notes/${bargainingUnitId}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setNotes((await res.json()) || []);
    } catch (e) { console.error('Failed to fetch notes:', e); }
    finally { setLoading(false); }
  };

  const addNote = async () => {
    const safe = sanitize(newNote);
    if (!toPlainText(safe)) return;
    const token = authStorage.getToken();
    if (!token) return;
    try {
      const res = await fetch('/api/notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          bargainingUnitId,
          note: safe,
          datetime: new Date().toISOString(),
          employeeName: username,
          isImportant: newNoteImportant,
        }),
      });
      const data = await res.json();
      setNotes([{ ...data, note: toHtml(data?.note || safe) }, ...notes]);
      setNewNote(''); setNewNoteImportant(false);
      clearDraft(draftKeyNew); setIsComposerOpen(false);
    } catch (e) { console.error('Failed to add note:', e); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    const token = authStorage.getToken();
    if (!token) return;
    try {
      const res = await fetch(`/api/notes/${noteId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setNotes(notes.filter(n => n.id !== noteId));
    } catch (e) { console.error('Failed to delete note:', e); }
  };

  const updateNote = async (noteId, html) => {
    const token = authStorage.getToken();
    if (!token) return;
    try {
      const res = await fetch(`/api/notes/${noteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ note: sanitize(html) }),
      });
      if (res.ok) {
        setNotes(notes.map(n => n.id === noteId ? { ...n, note: toHtml(html) } : n));
        clearDraft(draftKeyEdit(noteId)); setEditingNoteId(null); setEditContent('');
        trackEvent(noteId, 'edit_save');
      }
    } catch (e) { console.error('Failed to update note:', e); }
  };

  const toggleImportant = async (noteId) => {
    const note = notes.find(n => n.id === noteId);
    if (!note) return;
    const token = authStorage.getToken();
    if (!token) return;
    try {
      const res = await fetch(`/api/notes/${noteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ isImportant: !note.isImportant }),
      });
      if (res.ok) {
        setNotes(notes.map(n => n.id === noteId ? { ...n, isImportant: !n.isImportant } : n));
        trackEvent(noteId, note.isImportant ? 'unmark_important' : 'mark_important');
      }
    } catch (e) { console.error('Failed to toggle important:', e); }
  };

  const startEdit = (note) => {
    setEditingNoteId(note.id);
    const draft = loadDraft(draftKeyEdit(note.id));
    setEditContent(draft || toHtml(note.note));
    trackEvent(note.id, 'edit_start');
  };

  const cancelEdit = (noteId) => {
    clearDraft(draftKeyEdit(noteId)); setEditingNoteId(null); setEditContent('');
  };



  const formatFull = (ds) => {
    try { return new Date(ds).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return ds; }
  };

  const filteredNotes = notes.filter(note => {
    const plain = toPlainText(toHtml(note.note)).toLowerCase();
    const matches = plain.includes(searchTerm.toLowerCase()) ||
      (note.employeeName || '').toLowerCase().includes(searchTerm.toLowerCase());
    if (filterType === 'recent') return matches && (new Date() - new Date(note.datetime)) / 86400000 <= 7;
    if (filterType === 'important') return matches && note.isImportant;
    return matches;
  });

  const isOwner = (note) =>
    String(note.employeeName || '').toLowerCase() === String(username || '').toLowerCase();

  const iconBtn = (onClick, title, color, children, active) => (
    <button
      onClick={onClick}
      title={title}
      style={{
        background: active ? 'var(--bg-accent-soft)' : 'none',
        border: 'none', cursor: 'pointer',
        padding: '3px 6px', color, display: 'flex', alignItems: 'center',
        borderRadius: 4, lineHeight: 1, transition: 'background 120ms',
      }}
    >
      {children}
    </button>
  );

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-head">
        <StickyNote size={15} style={{ color: 'var(--accent)', flexShrink: 0 }} />
        <h3 style={{ margin: 0 }}>Notes</h3>
        {!loading && <span className="badge" style={{ marginLeft: 4 }}>{notes.length}</span>}
        <button
          className={`btn btn-sm ${isComposerOpen ? 'btn-secondary' : 'btn-primary'}`}
          style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5 }}
          onClick={() => {
            const next = !isComposerOpen;
            setIsComposerOpen(next);
            if (!next) { setNewNote(''); setNewNoteImportant(false); clearDraft(draftKeyNew); }
          }}
        >
          {isComposerOpen ? <><X size={13} />Cancel</> : <><Plus size={13} />New Note</>}
        </button>
      </div>

      <div style={{ padding: '0 16px 16px' }}>

        {/* Composer */}
        {isComposerOpen && (
          <div style={{ marginBottom: 16, background: 'var(--bg-sunken)', borderRadius: 'var(--radius-md)', padding: '12px 14px', border: newNoteImportant ? '1px solid var(--accent)' : '1px solid transparent' }}>
            <RichTextEditor
              value={newNote}
              onChange={setNewNote}
              onSubmit={addNote}
              placeholder="Type your note here…"
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={addNote}
                disabled={!toPlainText(sanitize(newNote))}
              >
                Save Note
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => { setIsComposerOpen(false); setNewNote(''); setNewNoteImportant(false); clearDraft(draftKeyNew); }}
              >
                Discard
              </button>
              {/* Important toggle in composer */}
              <button
                onClick={() => setNewNoteImportant(v => !v)}
                title={newNoteImportant ? 'Remove important flag' : 'Mark as important'}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: newNoteImportant ? 'var(--bg-accent-soft)' : 'none',
                  border: `1px solid ${newNoteImportant ? 'var(--accent)' : 'var(--border-subtle)'}`,
                  borderRadius: 6, cursor: 'pointer', padding: '3px 10px',
                  color: newNoteImportant ? 'var(--accent)' : 'var(--fg-3)',
                  fontSize: 12.5, fontWeight: newNoteImportant ? 600 : 400,
                  transition: 'all 120ms',
                }}
              >
                <Star size={13} fill={newNoteImportant ? 'currentColor' : 'none'} />
                {newNoteImportant ? 'Important' : 'Mark important'}
              </button>
              <span style={{ fontSize: 11.5, color: 'var(--fg-3)', marginLeft: 'auto' }}>Draft auto-saves</span>
            </div>
          </div>
        )}

        {/* Search + filters */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
          <div className="search-box" style={{ flex: 1, minWidth: 160 }}>
            <input
              type="text"
              placeholder="Search notes…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="pills">
            {['all', 'recent', 'important'].map(f => (
              <button
                key={f}
                className={`pill${filterType === f ? ' active' : ''}`}
                onClick={() => setFilterType(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Notes list */}
        {loading ? (
          <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--fg-3)', fontSize: 13.5 }}>Loading…</div>
        ) : filteredNotes.length === 0 ? (
          <div style={{ padding: '36px 0', textAlign: 'center', color: 'var(--fg-3)', fontSize: 13.5 }}>
            {searchTerm
              ? 'No notes match your search'
              : filterType === 'important' ? 'No starred notes'
              : filterType === 'recent' ? 'No notes in the last 7 days'
              : 'No notes yet — add the first one'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filteredNotes.map(note => (
              <div
                key={note.id}
                style={{
                  border: '1px solid var(--border-subtle)',
                  borderLeft: note.isImportant ? '3px solid var(--accent)' : '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  background: note.isImportant ? 'var(--bg-accent-soft)' : 'var(--bg-surface)',
                  overflow: 'hidden',
                }}
              >
                {/* Note meta bar */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px',
                  borderBottom: '1px solid var(--border-subtle)',
                  background: 'var(--bg-sunken)',
                }}>
                  {/* Avatar initial */}
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--accent)', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700,
                  }}>
                    {(note.employeeName || '?').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)', lineHeight: 1.2 }}>
                      {note.employeeName || 'Unknown'}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--fg-3)', lineHeight: 1.2 }} title={formatFull(note.datetime)}>
                      {formatFull(note.datetime)}
                    </div>
                  </div>
                  {note.isImportant && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                      padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                      background: 'var(--accent)', color: '#fff', letterSpacing: '0.04em',
                    }}>
                      <Star size={10} fill="currentColor" /> Important
                    </span>
                  )}
                  {/* Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginLeft: 'auto' }}>
                    {iconBtn(
                      () => toggleImportant(note.id),
                      note.isImportant ? 'Remove important flag' : 'Mark as important',
                      note.isImportant ? 'var(--accent)' : 'var(--fg-3)',
                      <Star size={14} fill={note.isImportant ? 'currentColor' : 'none'} />,
                      note.isImportant
                    )}
                    {isOwner(note) && editingNoteId !== note.id && iconBtn(
                      () => startEdit(note),
                      'Edit note',
                      'var(--fg-3)',
                      <Pencil size={13} />
                    )}
                    {isOwner(note) && iconBtn(
                      () => deleteNote(note.id),
                      'Delete note',
                      'var(--color-danger)',
                      <Trash2 size={13} />
                    )}
                  </div>
                </div>

                {/* Note body */}
                <div style={{ padding: '12px 14px' }}>
                  {editingNoteId === note.id ? (
                    <>
                      <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)', marginBottom: 10 }}>
                        <RichTextEditor
                          value={editContent}
                          onChange={setEditContent}
                          onSubmit={() => updateNote(note.id, editContent)}
                          placeholder="Edit note…"
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button className="btn btn-primary btn-sm" onClick={() => updateNote(note.id, editContent)}>Save</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => cancelEdit(note.id)}>Cancel</button>
                        <span style={{ fontSize: 11.5, color: 'var(--fg-3)', marginLeft: 2 }}>Draft auto-saves</span>
                      </div>
                    </>
                  ) : (
                    <div
                      className="note-body-html"
                      style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--fg-1)' }}
                      dangerouslySetInnerHTML={{ __html: toHtml(note.note) }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesPanel;
